import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Siren, MapPin, Radio, ShieldPlus, HeartPulse, Flame, Loader2, Signal, Clock3, AlertOctagon, Power, Wifi, BatteryMedium, ScrollText, UserRound, Search, Map as MapIcon, LayoutDashboard, X, Building2, Gavel, FileText, Scale, MessageSquare, PanelLeftClose, PanelLeftOpen, StickyNote, Binoculars } from "lucide-react";
import api from "@/lib/api";
import { PreviewRibbon, DEMO, NcdbDisclaimer } from "@/components/cef/cefShared";
import { usePhoneAudio } from "@/components/cef/phoneAudio";
import DeptView from "@/components/cef/mdt/DeptView";
import ReportForm from "@/components/cef/mdt/ReportForm";
import WarrantForm from "@/components/cef/mdt/WarrantForm";
import BeatForm from "@/components/cef/mdt/BeatForm";
import BootScreen from "@/components/cef/mdt/BootScreen";
import TutorialOverlay from "@/components/cef/mdt/TutorialOverlay";
import CourtView from "@/components/cef/mdt/CourtView";
import ChatDrawer from "@/components/cef/mdt/ChatDrawer";
import { DEMO_BEATS, DEMO_CHAT, DEMO_NOTES } from "@/components/cef/mdt/mdtDemo";
import { activePack } from "@/components/cef/packs";
import { HelpCircle, Hexagon } from "lucide-react";
import { useI18n } from "@/i18n";

/* NC-MDT — ruggedized police data terminal (CEF), Toughbook-class tablet.
   Hardware shell (rubber bumpers, LEDs, hard keys) around a full CAD OS:
   unit status board (live 10-codes), priority call queue fed by the AI-911 phone
   intake, dispatch with unit assignment, enroute/on-scene/close lifecycle, and
   caller loop-back — status changes text the caller's in-game phone.
   Live: /cef/cad/:serverId?token=<player-session>. No token → demo floor. */

const CAT = { police: { icon: ShieldPlus, cls: "text-sky-400 border-sky-500/50" }, ems: { icon: HeartPulse, cls: "text-emerald-400 border-emerald-500/50" }, fire: { icon: Flame, cls: "text-orange-400 border-orange-500/50" }, multi: { icon: Siren, cls: "text-red-400 border-red-500/50" } };
const PRI = { 1: { bar: "#ef4444", label: "P1 · IMMEDIATE", cls: "text-red-400 border-red-500/50 bg-red-500/10" }, 2: { bar: "#fb923c", label: "P2 · URGENT", cls: "text-orange-400 border-orange-500/50" }, 3: { bar: "#fbbf24", label: "P3 · ROUTINE", cls: "text-amber-300 border-amber-500/40" }, 4: { bar: "#60a5fa", label: "P4 · LOW", cls: "text-blue-300 border-blue-500/40" } };
const FLOW = ["open", "dispatched", "enroute", "onscene", "closed"];
const STATUS_CLS = { open: "text-red-400 border-red-500/50 bg-red-500/10", dispatched: "text-orange-400 border-orange-500/50", enroute: "text-sky-300 border-sky-500/50", onscene: "text-emerald-300 border-emerald-500/50", closed: "text-zinc-500 border-white/15" };
const AGENCY_CLS = { police: "text-sky-400 border-sky-500/40", ems: "text-emerald-400 border-emerald-500/40", fire: "text-orange-400 border-orange-500/40" };
const UNIT_STATUS = { "10-8": "text-emerald-400", "10-6": "text-amber-400", "10-7": "text-red-400", "10-97": "text-sky-300" };
const NEXT = { open: ["dispatched", "cad.next.dispatch"], dispatched: ["enroute", "cad.next.enroute"], enroute: ["onscene", "cad.next.onscene"], onscene: ["closed", "cad.next.close"] };

const elapsed = (isoStr) => {
  const s = Math.max(0, Math.floor((Date.now() - new Date(isoStr).getTime()) / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const DEMO_BOLOS = [
  { id: "b1", text: "Black Sultan RS, plate 84-XKQ — felony evade, armed & dangerous", at: "23:12" },
  { id: "b2", text: "Yellow racing jacket male — lumber yard shooting person of interest", at: "23:38" },
];
const DEMO_TRANSCRIPT = [
  { role: "dispatcher", text: "911, what's your emergency?" },
  { role: "caller", text: "Someone broke into the store across the street, I can hear the alarm" },
  { role: "dispatcher", text: "Copy. What's the address or nearest cross street?" },
  { role: "caller", text: "24/7 on Harbor Boulevard, near the beach" },
];

export default function CadCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;
  const sfx = usePhoneAudio();
  const { t } = useI18n();

  const [alerts, setAlerts] = useState(null);
  const [units, setUnits] = useState([]);
  const [myUnit, setMyUnit] = useState(null);
  const [filter, setFilter] = useState("open");
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null);
  const [clock, setClock] = useState("");
  const [, setTick] = useState(0);
  const [busy, setBusy] = useState("");
  const [view, setView] = useState("board"); // board | map
  const [rail, setRail] = useState("units"); // units | ncic
  const [bolos, setBolos] = useState([]);
  const [boloText, setBoloText] = useState("");
  const [lookupQ, setLookupQ] = useState("");
  const [lookupRes, setLookupRes] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [me, setMe] = useState(demo ? { playercode: 22110, name: "Sgt. A. King", rank: { name: "Sergeant" }, dept: { name: "Police Department" }, is_command: true, is_supervisor: true, bootstrap: false, roster: { duty_since: new Date(Date.now() - 42 * 60000).toISOString(), tutorial_seen: [] } } : null);
  const [reportFor, setReportFor] = useState(null);
  const [warrantFor, setWarrantFor] = useState(0);
  const [booted, setBooted] = useState(false);
  const [tutorial, setTutorial] = useState(false);
  const [beats, setBeats] = useState([]);
  const [drawMode, setDrawMode] = useState(false);
  const [beatEdit, setBeatEdit] = useState(null); // {rect} create | {beat} edit
  const [deptTab, setDeptTab] = useState("roster");
  const [railOpen, setRailOpen] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [panicArm, setPanicArm] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const chatSeen = useRef(0);
  const known = useRef(new Set());
  const mapInit = useRef(false);

  useEffect(() => {
    const t = setInterval(() => { setClock(new Date().toLocaleTimeString([], { hour12: false })); setTick((x) => x + 1); }, 1000);
    const unlock = () => sfx.ensure();
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => { clearInterval(t); window.removeEventListener("pointerdown", unlock); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(() => {
    if (demo) {
      setAlerts(DEMO.cadAlerts.filter((a) => !filter || a.status === filter));
      setUnits(DEMO.cadUnits);
      setBolos(DEMO_BOLOS);
      if (!mapInit.current) {
        mapInit.current = true;
        setMapData({
          alerts: DEMO.cadAlerts.filter((a) => a.status !== "closed").map((a) => ({ id: a.id, ref: a.ref, priority: a.priority, status: a.status, category: a.category, via: a.via || "", summary: a.summary.slice(0, 120), x: a.location.x, y: a.location.y })),
          units: DEMO.cadUnits.map((u, i) => ({ callsign: u.callsign, role: u.agency, status: u.status, x: [-552, 24, 215, -210, 180, -95][i % 6] + i * 40, y: [5320, -1346, -2100, 190, 480, -950][i % 6] - i * 25 })),
          beats: DEMO_BEATS,
        });
        setBeats(DEMO_BEATS);
      }
      setErr("");
      return;
    }
    api.get(`/nc/servers/${serverId}/cad/alerts`, { params: { token, status: filter } })
      .then((r) => {
        const fresh = r.data.filter((a) => !known.current.has(a.id));
        if (fresh.length && known.current.size) {
          sfx.dispatchChirp(); // radio chirp on fresh 911 intake
          try { window.ue?.gamebridge?.newcadalert?.(); } catch { /* no bridge in preview */ }
        }
        r.data.forEach((a) => known.current.add(a.id));
        setAlerts(r.data);
        setErr("");
      })
      .catch((e) => setErr(e.response?.data?.detail || t("cad.offline")));
    api.get(`/nc/servers/${serverId}/cad/units`, { params: { token } })
      .then((r) => { setUnits(r.data.units); setMyUnit((cur) => r.data.units.find((u) => u.callsign === cur?.callsign) || cur); })
      .catch(() => {});
    api.get(`/nc/servers/${serverId}/cad/bolos`, { params: { token } }).then((r) => setBolos(r.data.bolos)).catch(() => {});
    api.get(`/nc/servers/${serverId}/cad/missions`, { params: { token } }).catch(() => {}); // lazy-spawns dispatch missions into the call board
    if (view === "map") api.get(`/nc/servers/${serverId}/cad/map`, { params: { token } }).then((r) => { setMapData(r.data); setBeats(r.data.beats || []); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo, serverId, token, filter, view]);

  useEffect(() => {
    known.current = new Set();
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  // panic: two-step arm -> confirm, broadcasts a P1 to every unit
  const panic = async () => {
    sfx.keyTick();
    if (!panicArm) { setPanicArm(true); setTimeout(() => setPanicArm(false), 3000); return; }
    setPanicArm(false);
    sfx.dispatchChirp();
    if (demo) {
      const p = { id: `panic-${Date.now()}`, ref: "1099-DEMO", caller: "1-ADAM-12 (Sgt. A. King)", summary: "PANIC BUTTON — 1-ADAM-12 needs immediate assistance. All units converge.", category: "police", priority: 1, services: ["police"], location: { label: "Last known position", x: -552, y: 5320 }, status: "open", via: "panic", created_at: new Date().toISOString() };
      setAlerts((a) => [p, ...(a || [])]);
      setMapData((m) => m ? { ...m, alerts: [...m.alerts, { id: p.id, ref: p.ref, priority: 1, status: "open", category: "police", via: "panic", summary: p.summary.slice(0, 120), x: -552, y: 5320 }] } : m);
      import("sonner").then(({ toast }) => toast.error("10-99 PANIC broadcast (demo floor)"));
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/cad/panic`, {}, { params: { token } });
      import("sonner").then(({ toast }) => toast.error(r.data.message));
      load();
    } catch (e) { import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Panic failed")); }
  };

  // chat unread badge (light poll while drawer is closed)
  useEffect(() => {
    if (demo || chatOpen) return;
    const pull = () => api.get(`/nc/servers/${serverId}/cad/chat`, { params: { token } })
      .then((r) => { const n = r.data.messages.length; if (n > chatSeen.current && chatSeen.current > 0) setUnread((u) => u + (n - chatSeen.current)); chatSeen.current = n; }).catch(() => {});
    pull();
    const t = setInterval(pull, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo, chatOpen, serverId, token]);

  // call notes thread for the selected alert
  const selId = (alerts || []).find((a) => a.id === sel)?.id || (alerts || [])[0]?.id;
  useEffect(() => {
    setNoteDraft("");
    if (!selId) { setNotes([]); return; }
    if (demo) { setNotes(DEMO_NOTES); return; }
    api.get(`/nc/servers/${serverId}/cad/alerts/${selId}/notes`, { params: { token } }).then((r) => setNotes(r.data.notes)).catch(() => setNotes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId]);

  const addNote = async () => {
    if (!noteDraft.trim() || !selAlert) return;
    sfx.keyTick();
    if (demo) { setNotes((n) => [...n, { id: `l${Date.now()}`, author: "1-ADAM-12", body: noteDraft.trim(), at: new Date().toISOString() }]); setNoteDraft(""); return; }
    try {
      await api.post(`/nc/servers/${serverId}/cad/alerts/${selAlert.id}/notes`, { body: noteDraft.trim() }, { params: { token } });
      setNoteDraft("");
      const r = await api.get(`/nc/servers/${serverId}/cad/alerts/${selAlert.id}/notes`, { params: { token } });
      setNotes(r.data.notes);
    } catch { /* next load */ }
  };

  // dept identity/perms for the Department view (auto-enrols the officer server-side)
  useEffect(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/cad/dept/me`, { params: { token } }).then((r) => setMe(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo, serverId, token]);

  // first-run field training: auto-plays once per role section, skippable but counts
  useEffect(() => {
    if (!booted || !me) return;
    const seen = demo
      ? JSON.parse(localStorage.getItem("nc_mdt_tut_v1") || "[]")
      : (me.roster?.tutorial_seen || []);
    const applicable = ["officer", me.is_supervisor && "supervisor", me.is_command && "command"].filter(Boolean);
    const needed = applicable.filter((s) => !seen.includes(s));
    if (needed.length) setTutorial({ sections: needed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booted, me]);

  const finishTutorial = () => {
    const sections = tutorial?.sections || [];
    setTutorial(null);
    if (demo) {
      const cur = JSON.parse(localStorage.getItem("nc_mdt_tut_v1") || "[]");
      localStorage.setItem("nc_mdt_tut_v1", JSON.stringify([...new Set([...cur, ...sections])]));
      return;
    }
    sections.forEach((s) => api.post(`/nc/servers/${serverId}/cad/dept/tutorial`, { section: s }, { params: { token } }).catch(() => {}));
  };

  // live map: fast 3s polling while the map is up so bridge fixes stream in real time
  useEffect(() => {
    if (demo || view !== "map") return;
    const pull = () => api.get(`/nc/servers/${serverId}/cad/map`, { params: { token } }).then((r) => { setMapData(r.data); setBeats(r.data.beats || []); }).catch(() => {});
    pull();
    const t = setInterval(pull, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, demo, serverId, token]);

  const setStatus = async (alert, status) => {
    sfx.keyTick();
    if (demo) {
      setAlerts((a) => a.map((x) => (x.id === alert.id ? { ...x, status, assigned_unit: myUnit?.callsign || x.assigned_unit } : x)).filter((x) => !filter || x.status === filter));
      return;
    }
    setBusy(alert.id);
    try {
      await api.post(`/nc/servers/${serverId}/cad/alerts/${alert.id}/status`, { status, unit: myUnit?.callsign || "" }, { params: { token } });
      sfx.dispatchChirp();
      load();
    } catch { /* surfaced on next poll */ } finally { setBusy(""); }
  };

  // dispatch missions: resolving officer marks complete -> reward wires to bank
  const completeMission = async (alert) => {
    sfx.keyTick();
    if (demo) {
      setAlerts((a) => a.map((x) => (x.id === alert.id ? { ...x, status: "closed" } : x)).filter((x) => !filter || x.status === filter));
      import("sonner").then(({ toast }) => toast.success(`${alert.mission?.title || "Mission"} complete — $${alert.mission?.reward ?? 0} wired to your bank (demo)`));
      return;
    }
    setBusy(alert.id);
    try {
      const r = await api.post(`/nc/servers/${serverId}/cad/missions/${alert.id}/complete`, {}, { params: { token } });
      sfx.dispatchChirp();
      import("sonner").then(({ toast }) => toast.success(r.data.message));
      load();
    } catch (e) {
      import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Could not complete mission"));
    } finally { setBusy(""); }
  };

  // surveillance van: detectives stage on wiretap intercepts — set in 3 min, +75% on resolve
  const deployVan = async (alert) => {
    sfx.keyTick();
    const unit = myUnit?.callsign || "DET-1";
    if (demo) {
      setAlerts((a) => a.map((x) => (x.id === alert.id ? { ...x, mission: { ...x.mission, stakeout: { unit, ready_at: new Date(Date.now() + 180000).toISOString() } } } : x)));
      import("sonner").then(({ toast }) => toast.success(`Surveillance van rolling — ${unit} set in ~3 min (demo)`));
      return;
    }
    setBusy(alert.id);
    try {
      const r = await api.post(`/nc/servers/${serverId}/cad/missions/${alert.id}/stakeout`, { unit }, { params: { token } });
      sfx.dispatchChirp();
      import("sonner").then(({ toast }) => toast.success(r.data.message));
      load();
    } catch (e) {
      import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Could not deploy the van"));
    } finally { setBusy(""); }
  };

  const setMyStatus = async (code) => {
    sfx.keyTick();
    if (demo) { setMyUnit({ callsign: "1-ADAM-12", name: "Demo Officer", status: code, role: "police" }); return; }
    try {
      const r = await api.post(`/nc/servers/${serverId}/cad/units/status`, { status: code, callsign: myUnit?.callsign || "" }, { params: { token } });
      setMyUnit({ callsign: r.data.callsign, status: code });
      load();
    } catch { /* next poll */ }
  };

  const postBolo = async () => {
    if (boloText.trim().length < 5) return;
    sfx.keyTick();
    if (demo) {
      setBolos((b) => [{ id: `l${Date.now()}`, text: boloText.trim(), author: "1-ADAM-12", at: new Date().toISOString() }, ...b]);
      setBoloText("");
      return;
    }
    setBusy("bolo");
    try {
      const r = await api.post(`/nc/servers/${serverId}/cad/bolos`, { text: boloText.trim() }, { params: { token } });
      import("sonner").then(({ toast }) => toast.success(r.data.message));
      setBoloText("");
      load();
    } catch { /* next poll */ } finally { setBusy(""); }
  };

  const clearBolo = async (id) => {
    sfx.keyTick();
    if (demo) { setBolos((b) => b.filter((x) => x.id !== id)); return; }
    try { await api.post(`/nc/servers/${serverId}/cad/bolos/${id}/clear`, {}, { params: { token } }); load(); } catch { /* next poll */ }
  };

  const runLookup = async () => {
    if (lookupQ.trim().length < 2) return;
    sfx.keyTick();
    setBusy("lookup");
    if (demo) {
      setLookupRes({ results: [{ playercode: 22110, name: "Ada King", faction: "BPD", phone_registered: true, reports: 0, calls_911: 1, flags: [], wanted: false, license: { points: 2, suspended: false } }, { playercode: 77003, name: "Dwight Kingsley", faction: "", phone_registered: true, reports: 2, calls_911: 0, flags: ["WANTED — Armed robbery of the 24/7 · FUNDS LOCKED", "LICENCE SUSPENDED (14 PTS)"], wanted: true, license: { points: 14, suspended: true } }], vehicles: lookupQ.length > 3 ? [{ plate: lookupQ.toUpperCase(), owner: "A. O'Connor", model: "Vapid Interceptor" }] : [], query: lookupQ });
      setBusy("");
      return;
    }
    try { const r = await api.get(`/nc/servers/${serverId}/cad/lookup`, { params: { token, q: lookupQ.trim() } }); setLookupRes(r.data); }
    catch { setLookupRes({ results: [], vehicles: [], query: lookupQ }); } finally { setBusy(""); }
  };

  const selAlert = (alerts || []).find((a) => a.id === sel) || (alerts || [])[0] || null;
  const openP1 = (alerts || []).filter((a) => a.status === "open" && a.priority === 1).length;

  return (
    <div data-testid="cef-cad" className="min-h-screen bg-[#0b0d10] flex items-center justify-center p-3 sm:p-6 relative overflow-hidden" style={{ backgroundImage: "radial-gradient(80% 60% at 50% 0%, rgba(56,120,255,0.06), transparent 70%)" }}>
      {demo && <PreviewRibbon what="police MDT" />}
      <NcdbDisclaimer />

      {/* closing-report modal (files into the courthouse docket) */}
      {reportFor && (
        <ReportForm serverId={serverId} token={token} demo={demo} alert={reportFor}
          onDone={() => { setReportFor(null); if (!demo) load(); else setAlerts((a) => a.map((x) => x.id === reportFor.id ? { ...x, status: "closed" } : x)); }}
          onCancel={() => setReportFor(null)} />
      )}

      {/* ============ TOUGHBOOK SHELL ============ */}
      <div className={`relative w-full max-w-[1240px] rounded-[2.2rem] p-[14px] pt-[34px] pb-[46px] bg-gradient-to-b from-zinc-700 via-zinc-800 to-zinc-900 shadow-[0_50px_140px_rgba(0,0,0,0.95),inset_0_2px_0_rgba(255,255,255,0.12)] ${demo ? "mt-7" : ""}`}>
        {/* rubber corner bumpers */}
        {["-top-2 -left-2", "-top-2 -right-2", "-bottom-2 -left-2", "-bottom-2 -right-2"].map((pos) => (
          <span key={pos} className={`absolute ${pos} w-10 h-10 rounded-[1rem] bg-zinc-800 border border-zinc-600/60 shadow-[inset_0_2px_3px_rgba(255,255,255,0.15),0_4px_10px_rgba(0,0,0,0.6)]`} />
        ))}
        {/* top bezel: sensors + brand */}
        <div className="absolute top-0 inset-x-0 h-[34px] flex items-center justify-center gap-3">
          <span className="font-mono text-[8px] tracking-[0.4em] text-zinc-400 uppercase absolute left-8">SPECPAD · MDT-9</span>
          <span className="w-2 h-2 rounded-full bg-zinc-900 border border-zinc-600" />
          <span className="w-1.5 h-1.5 rounded-full bg-zinc-900 border border-zinc-600" />
          <span className="w-1.5 h-1.5 rounded-full bg-zinc-600/40" />
          <span className="font-mono text-[8px] tracking-[0.4em] text-zinc-400 uppercase absolute right-8">FIELD UNIT</span>
        </div>

        {/* ============ SCREEN ============ */}
        <div className="relative rounded-2xl overflow-hidden bg-[#070a10] text-zinc-100 flex flex-col h-[720px] max-h-[78vh] border-2 border-black shadow-[inset_0_0_60px_rgba(0,0,0,0.7)]" style={{ fontFamily: "'Rajdhani','Segoe UI',sans-serif", filter: activePack("cad_theme")?.pack?.cad?.filter || undefined }}>
          {/* cold boot */}
          {!booted && <BootScreen onDone={() => setBooted(true)} />}
          {/* first-run field training */}
          {tutorial && booted && (
            <TutorialOverlay sections={tutorial.sections} onFinish={finishTutorial}
              onNavigate={(v, r, t) => { if (v) setView(v); if (r) setRail(r); if (t) setDeptTab(t); }} />
          )}
          {/* beat planner modal */}
          {beatEdit && (
            <BeatForm serverId={serverId} token={token} demo={demo}
              rect={beatEdit.rect} beat={beatEdit.beat} mapUnits={mapData?.units || []}
              onDone={(b) => {
                setBeatEdit(null);
                if (demo) setBeats((bs) => beatEdit.beat ? bs.map((x) => x.id === b.id ? b : x) : [...bs, b]);
                else load();
              }}
              onCancel={() => setBeatEdit(null)}
              onDelete={beatEdit.beat ? async () => {
                if (demo) { setBeats((bs) => bs.filter((x) => x.id !== beatEdit.beat.id)); setBeatEdit(null); return; }
                try { await api.delete(`/nc/servers/${serverId}/cad/beats/${beatEdit.beat.id}`, { params: { token } }); } catch { /* surfaced on reload */ }
                setBeatEdit(null); load();
              } : undefined} />
          )}
          {/* OS header */}
          <div className="flex items-center gap-3 px-4 py-2 border-b border-white/10 bg-black/60">
            <Siren className={`w-4 h-4 ${openP1 ? "text-red-400 animate-pulse" : "text-red-500/70"}`} />
            <h1 className="font-bold uppercase tracking-[0.3em] text-[13px]">NC-MDT</h1>
            <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 border border-white/10 px-1.5 py-0.5 hidden sm:inline">{serverId}</span>
            <div className="flex border border-white/15 ml-1">
              {[["board", LayoutDashboard, t("cad.board")], ["map", MapIcon, t("cad.map")], ["dept", Building2, t("cad.dept")], ["court", Scale, t("cad.court")]].map(([v, Icon, l]) => (
                <button key={v} data-testid={`cad-view-${v}`} onClick={() => { setView(v); sfx.keyTick(); }}
                  className={`font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 flex items-center gap-1.5 transition-colors ${view === v ? "bg-sky-500/20 text-sky-300" : "text-zinc-500 hover:text-zinc-200"}`}>
                  <Icon className="w-3 h-3" />{l}
                </button>
              ))}
            </div>
            <span className="ml-auto font-mono text-[9px] text-zinc-500 flex items-center gap-3.5">
              <button data-testid="cad-chat-open" onClick={() => { setChatOpen(!chatOpen); sfx.keyTick(); }} title={t("cad.chat")} className={`relative transition-colors ${chatOpen ? "text-sky-300" : "text-zinc-500 hover:text-sky-300"}`}>
                <MessageSquare className="w-3.5 h-3.5" />
                {unread > 0 && !chatOpen && <span data-testid="chat-badge" className="absolute -top-1.5 -right-1.5 min-w-3 h-3 px-0.5 rounded-full bg-red-500 text-black font-bold text-[7px] flex items-center justify-center">{unread}</span>}
              </button>
              <button data-testid="cad-help" onClick={() => { setTutorial({ sections: ["officer", me?.is_supervisor && "supervisor", me?.is_command && "command"].filter(Boolean) }); sfx.keyTick(); }} title={t("cad.training")} className="text-zinc-500 hover:text-sky-300 transition-colors">
                <HelpCircle className="w-3.5 h-3.5" />
              </button>
              <span className="hidden md:flex items-center gap-1"><Signal className="w-3 h-3 text-emerald-400" /> LTE</span>
              <span className="hidden md:flex items-center gap-1"><Wifi className="w-3 h-3 text-sky-400" /></span>
              <span className="hidden md:flex items-center gap-1"><BatteryMedium className="w-3.5 h-3.5 text-emerald-400" /> 87%</span>
              <span className="flex items-center gap-1.5 text-zinc-300"><Clock3 className="w-3 h-3" />{clock}</span>
            </span>
          </div>

          {/* my unit bar */}
          <div className="flex items-center gap-3 px-4 py-1.5 border-b border-white/10 bg-sky-500/[0.04]">
            <UserRound className="w-3.5 h-3.5 text-sky-400" />
            <span data-testid="cad-myunit" className="font-mono text-[10px] font-bold text-sky-300">{myUnit ? `${myUnit.callsign} · ${myUnit.status}` : t("cad.offduty")}</span>
            {me?.roster?.duty_since && myUnit?.status !== "10-7" && (
              <span data-testid="duty-timer" className="font-mono text-[8px] uppercase tracking-[0.15em] text-emerald-300 border border-emerald-500/40 px-1.5 py-0.5">{t("cad.onduty")} {elapsed(me.roster.duty_since)}</span>
            )}
            <div className="flex gap-1 ml-2">
              {["10-8", "10-6", "10-7", "10-97"].map((code) => (
                <button key={code} data-testid={`cad-my-${code}`} onClick={() => setMyStatus(code)}
                  className={`font-mono text-[9px] font-bold px-2 py-1 border transition-colors ${myUnit?.status === code ? "border-sky-400 text-sky-300 bg-sky-500/10" : "border-white/10 text-zinc-500 hover:text-zinc-200"}`}>
                  {code}
                </button>
              ))}
            </div>
            <button data-testid="cad-panic" onClick={panic}
              className={`ml-auto font-mono text-[9px] font-bold uppercase tracking-[0.15em] border px-3 py-1 transition-colors flex items-center gap-1.5 ${panicArm ? "border-red-400 bg-red-500/20 text-red-300 animate-pulse" : "border-red-500/40 text-red-400/80 hover:bg-red-500/10"}`}>
              <AlertOctagon className="w-3 h-3" /> {panicArm ? t("cad.panic_confirm") : t("cad.panic")}
            </button>
          </div>

          {err && !demo && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8 text-center">
              <Radio className="w-6 h-6 text-red-400" />
              <p data-testid="cad-error" className="font-mono text-[11px] text-red-400 max-w-md leading-relaxed">{err}</p>
              <p className="font-mono text-[9px] text-zinc-400 max-w-md">{t("cad.err_help")}</p>
            </div>
          )}
          {!alerts && !err && <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-sky-400" /></div>}

          {alerts && view === "map" && <MapPanel data={mapData} beats={beats} canCommand={!!me?.is_command} drawMode={drawMode} setDrawMode={setDrawMode} onBeatDraw={(rect) => { setBeatEdit({ rect }); setDrawMode(false); }} onBeatEdit={(b) => setBeatEdit({ beat: b })} />}

          {view === "dept" && <DeptView serverId={serverId} token={token} demo={demo} sfx={sfx} me={me} setMe={setMe} tab={deptTab} setTab={setDeptTab} />}

          {view === "court" && <CourtView serverId={serverId} token={token} demo={demo} sfx={sfx} />}

          <ChatDrawer serverId={serverId} token={token} demo={demo} open={chatOpen} onClose={() => { setChatOpen(false); setUnread(0); }} onSeen={() => setUnread(0)} />

          {alerts && view === "board" && (
            <div className="flex-1 flex flex-col min-h-0">
              {/* status filter strip */}
              <div className="flex items-center gap-1 px-3.5 py-1.5 border-b border-white/10 bg-black/30">
                {[["", t("cad.f_all")], ["open", t("cad.f_open")], ["dispatched", t("cad.f_dispatched")], ["enroute", t("cad.f_enroute")], ["onscene", t("cad.f_onscene")], ["closed", t("cad.f_closed")]].map(([s, l]) => (
                  <button key={s || "all"} data-testid={`cad-filter-${s || "all"}`} onClick={() => { setFilter(s); setSel(null); sfx.keyTick(); }}
                    className={`font-mono text-[8.5px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border transition-colors ${filter === s ? "border-sky-400 text-sky-300 bg-sky-500/10" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
                    {l}
                  </button>
                ))}
                <span className="ml-auto font-mono text-[8px] uppercase tracking-[0.15em] text-zinc-600">{(alerts || []).length} {t("cad.calls")}</span>
              </div>
            <div className="flex-1 flex min-h-0">
              {/* left rail: unit board + NCIC lookup (collapsible) */}
              <button data-testid="rail-toggle" onClick={() => { setRailOpen(!railOpen); sfx.keyTick(); }} title={railOpen ? t("cad.collapse") : `${t("cad.units")} & NCIC`}
                className="shrink-0 w-5 border-r border-white/10 bg-black/40 flex items-center justify-center text-zinc-600 hover:text-sky-300 transition-colors">
                {railOpen ? <PanelLeftClose className="w-3 h-3" /> : <PanelLeftOpen className="w-3 h-3" />}
              </button>
              {railOpen && (
              <aside className="w-[230px] shrink-0 border-r border-white/10 flex flex-col bg-black/40">
                <div className="flex border-b border-white/10">
                  {[["units", t("cad.units")], ["ncic", t("cad.ncic")]].map(([v, l]) => (
                    <button key={v} data-testid={`rail-${v}`} onClick={() => { setRail(v); sfx.keyTick(); }}
                      className={`flex-1 py-2 font-mono text-[8.5px] tracking-[0.2em] uppercase transition-colors ${rail === v ? "text-sky-300 border-b-2 border-sky-400 bg-sky-500/5" : "text-zinc-500 hover:text-zinc-300"}`}>{l}</button>
                  ))}
                </div>
                {rail === "units" && (
                  <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
                    <p className="px-3.5 pt-2 pb-1 font-mono text-[8px] tracking-[0.25em] uppercase text-zinc-600">{t("cad.on_duty")} · {units.length}</p>
                    {units.map((u) => (
                      <div key={u.callsign} data-testid={`cad-unit-${u.callsign}`} className="px-3.5 py-2.5 border-b border-white/5 flex items-center gap-2.5">
                        <span className={`w-1.5 h-8 border-l-2 ${AGENCY_CLS[u.role || u.agency] || AGENCY_CLS.police}`} />
                        <div className="min-w-0 flex-1">
                          <p className="font-mono text-[10px] font-bold text-zinc-100">{u.callsign}</p>
                          <p className="font-mono text-[8px] text-zinc-500 truncate">{u.name}{u.beat ? ` · ${u.beat}` : ""}</p>
                        </div>
                        <span className={`font-mono text-[9px] font-bold ${UNIT_STATUS[u.status] || "text-zinc-400"}`}>{u.status}</span>
                      </div>
                    ))}
                    {units.length === 0 && <p className="p-4 font-mono text-[8px] text-zinc-500 uppercase tracking-[0.15em] leading-relaxed">{t("cad.no_units")}</p>}
                  </div>
                )}
                {rail === "ncic" && (
                  <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2" style={{ scrollbarWidth: "thin" }}>
                    <p className="font-mono text-[8px] tracking-[0.25em] uppercase text-zinc-600">{t("cad.query")}</p>
                    <div className="flex gap-1.5">
                      <input data-testid="ncic-input" value={lookupQ} onChange={(e) => setLookupQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runLookup()} placeholder={t("cad.search_ph")}
                        className="flex-1 min-w-0 bg-black/60 border border-white/15 px-2.5 py-2 font-mono text-[10px] text-zinc-100 focus:outline-none focus:ring-1 focus:ring-sky-500" />
                      <button data-testid="ncic-run" onClick={runLookup} disabled={busy === "lookup"} className="px-2.5 border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 transition-colors disabled:opacity-40">
                        {busy === "lookup" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                    {lookupRes && (
                      <div className="flex flex-col gap-2">
                        {lookupRes.results.length === 0 && lookupRes.vehicles.length === 0 && <p data-testid="ncic-empty" className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.15em] py-2">No record for "{lookupRes.query}"</p>}
                        {lookupRes.results.map((c) => (
                          <div key={c.playercode} data-testid={`ncic-hit-${c.playercode}`} className="border border-white/10 bg-black/50 p-2.5 flex flex-col gap-1">
                            <p className="font-mono text-[10px] font-bold text-zinc-100">{c.name} <span className="text-zinc-500">#{c.playercode}</span>
                              {c.wanted && <span className="ml-1 font-mono text-[8px] text-red-300 border border-red-500/60 bg-red-500/20 px-1.5 py-0.5 animate-pulse">WANTED</span>}
                            </p>
                            <p className="font-mono text-[8.5px] text-zinc-400 leading-relaxed">
                              {c.faction && <span className="text-sky-300">{c.faction} · </span>}
                              phone {c.phone_registered ? "registered" : "unregistered"} · {c.calls_911} prior 911 call(s)
                            </p>
                            {c.license && (
                              <p data-testid={`ncic-license-${c.playercode}`} className={`font-mono text-[8px] font-bold uppercase border px-1.5 py-1 w-fit ${c.license.suspended ? "text-red-300 border-red-500/60 bg-red-500/15" : "text-zinc-300 border-white/15"}`}>
                                Licence: {c.license.suspended ? "SUSPENDED" : "valid"} · {c.license.points} pts
                              </p>
                            )}
                            {c.flags.map((f) => <p key={f} className={`font-mono text-[8px] font-bold border px-1.5 py-1 w-fit ${f.startsWith("WANTED") ? "text-red-300 border-red-500/60 bg-red-500/15" : "text-red-400 border-red-500/40 bg-red-500/10"}`}>⚠ {f}</p>)}
                            {!c.wanted && warrantFor !== c.playercode && (
                              <button data-testid={`ncic-warrant-${c.playercode}`} onClick={() => { setWarrantFor(c.playercode); sfx.keyTick(); }}
                                className="font-mono text-[8px] uppercase border border-red-500/40 text-red-300 px-1.5 py-1 w-fit hover:bg-red-500/10 transition-colors flex items-center gap-1">
                                <Gavel className="w-2.5 h-2.5" /> Issue warrant
                              </button>
                            )}
                            {warrantFor === c.playercode && (
                              <WarrantForm serverId={serverId} token={token} demo={demo} hit={c}
                                onDone={() => { setWarrantFor(0); runLookup(); }} />
                            )}
                          </div>
                        ))}
                        {lookupRes.vehicles.map((v) => (
                          <div key={v.plate} data-testid={`ncic-veh-${v.plate}`} className="border border-orange-500/30 bg-orange-500/5 p-2.5">
                            <p className="font-mono text-[10px] font-bold text-orange-300">{v.plate}</p>
                            <p className="font-mono text-[8.5px] text-zinc-400">{v.model} — registered to {v.owner}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {!lookupRes && <p className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em] leading-relaxed">Runs against citizen registry, faction rolls, reports, 911 history & vehicle plates.</p>}
                  </div>
                )}
              </aside>
              )}

              {/* call queue */}
              <main className="flex-1 min-w-0 overflow-y-auto p-3.5 flex flex-col gap-2" style={{ scrollbarWidth: "thin" }}>
                {alerts.length === 0 && (
                  <div className="flex-1 flex items-center justify-center">
                    <p data-testid="cad-empty" className="font-mono text-[11px] text-zinc-500 uppercase tracking-[0.3em]">No {filter} calls — city is quiet</p>
                  </div>
                )}
                {alerts.map((a) => {
                  const c = CAT[a.category] || CAT.police;
                  const p = PRI[a.priority] || PRI[3];
                  const Icon = c.icon;
                  const active = selAlert?.id === a.id;
                  const [nextStatus, nextLabel] = NEXT[a.status] || [];
                  const nextLabelT = nextLabel ? t(nextLabel) : null;
                  return (
                    <div key={a.id} data-testid={`cad-alert-${a.ref}`} role="button" tabIndex={0}
                      onClick={() => { setSel(a.id); sfx.keyTick(); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setSel(a.id); sfx.keyTick(); } }}
                      className={`text-left border bg-black/40 flex transition-colors hover:border-white/25 cursor-pointer ${active ? "border-white/30" : "border-white/10"}`}>
                      <span className="w-1 shrink-0" style={{ backgroundColor: p.bar, boxShadow: a.status === "open" ? `0 0 12px ${p.bar}` : "none" }} />
                      <div className="flex-1 p-3 flex flex-col gap-1.5 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`border px-1.5 py-0.5 ${c.cls}`}><Icon className="w-3.5 h-3.5" /></span>
                          <span className="font-mono text-[11px] font-bold text-sky-300">{a.ref}</span>
                          <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ${p.cls}`}>{p.label}</span>
                          <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ${STATUS_CLS[a.status] || STATUS_CLS.open}`}>{a.status}</span>
                          {a.via === "phone" && <span className="font-mono text-[8px] uppercase text-emerald-300 border border-emerald-500/40 px-1.5 py-0.5">911 phone</span>}
                          {a.via === "mission" && <span data-testid={`cad-mission-${a.ref}`} className="font-mono text-[8px] uppercase text-amber-300 border border-amber-500/50 px-1.5 py-0.5">Mission{a.mission?.reward ? ` · $${a.mission.reward}` : ""}</span>}
                          {(a.services || []).map((s) => <span key={s} className="font-mono text-[8px] uppercase text-zinc-400 border border-white/10 px-1.5 py-0.5">{s}</span>)}
                          {a.assigned_unit && <span className="font-mono text-[8px] uppercase text-sky-300 border border-sky-500/40 px-1.5 py-0.5">{a.assigned_unit}</span>}
                          <span className="ml-auto font-mono text-[10px] text-red-300 tabular-nums flex items-center gap-1"><Clock3 className="w-3 h-3" />{elapsed(a.created_at)}</span>
                        </div>
                        <p data-testid="cad-summary" className="text-[13px] text-zinc-100 leading-snug">{a.summary}</p>
                        <div className="flex items-center gap-3 flex-wrap font-mono text-[9px] text-zinc-500">
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-orange-400" />{a.location?.label || "location per caller"}{a.location?.x != null && <span className="text-zinc-400">({Math.round(a.location.x)}, {Math.round(a.location.y)})</span>}</span>
                          <span>caller: {a.caller}</span>
                          {a.status !== "closed" && (
                            <span className="ml-auto flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                              {nextStatus && (
                                <button data-testid={`cad-${nextStatus}-${a.ref}`} disabled={busy === a.id} onClick={() => setStatus(a, nextStatus)}
                                  className="font-mono text-[8px] uppercase border border-orange-500/50 text-orange-300 px-2.5 py-1.5 hover:bg-orange-500/10 transition-colors disabled:opacity-40 flex items-center gap-1">
                                  {busy === a.id && <Loader2 className="w-3 h-3 animate-spin" />}{nextLabelT}{myUnit ? ` (${myUnit.callsign})` : ""}
                                </button>
                              )}
                              {a.via === "mission" && a.mission?.template === "wiretap_intercept" && !a.mission?.stakeout && a.status !== "closed" && (
                                <button data-testid={`cad-stakeout-${a.ref}`} disabled={busy === a.id} onClick={() => deployVan(a)}
                                  className="font-mono text-[8px] uppercase border border-indigo-500/50 text-indigo-300 px-2.5 py-1.5 hover:bg-indigo-500/10 transition-colors disabled:opacity-40 flex items-center gap-1">
                                  <Binoculars className="w-3 h-3" />Deploy van
                                </button>
                              )}
                              {a.mission?.stakeout && (
                                <span data-testid={`cad-stakeout-state-${a.ref}`} className="font-mono text-[8px] uppercase border border-indigo-400/40 text-indigo-200 px-2 py-1.5 flex items-center gap-1">
                                  <Binoculars className="w-3 h-3" />{a.mission.stakeout.unit} · {new Date() >= new Date(a.mission.stakeout.ready_at) ? "van set · +75%" : "van en route"}
                                </span>
                              )}
                              {a.via === "mission" && (
                                <button data-testid={`cad-mission-complete-${a.ref}`} disabled={busy === a.id} onClick={() => completeMission(a)}
                                  className="font-mono text-[8px] uppercase border border-amber-500/50 text-amber-300 px-2.5 py-1.5 hover:bg-amber-500/10 transition-colors disabled:opacity-40 flex items-center gap-1">
                                  {busy === a.id && <Loader2 className="w-3 h-3 animate-spin" />}Complete{a.mission?.reward ? ` · $${a.mission.reward}` : ""}
                                </button>
                              )}
                              {a.status !== "onscene" && <button data-testid={`cad-close-${a.ref}`} onClick={() => setStatus(a, "closed")} className="font-mono text-[8px] uppercase border border-white/15 text-zinc-400 px-2.5 py-1.5 hover:border-white/40 transition-colors">Close</button>}
                              <button data-testid={`cad-report-${a.ref}`} onClick={() => { setReportFor(a); sfx.keyTick(); }}
                                className="font-mono text-[8px] uppercase border border-sky-500/50 text-sky-300 px-2.5 py-1.5 hover:bg-sky-500/10 transition-colors flex items-center gap-1">
                                <FileText className="w-3 h-3" /> Report
                              </button>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </main>

              {/* right rail: BOLO + selected call + transcript */}
              <aside className="w-[300px] shrink-0 border-l border-white/10 flex flex-col bg-black/40">
                <p className="px-3.5 py-2 border-b border-white/10 font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-2"><AlertOctagon className="w-3.5 h-3.5 text-orange-400" /> BOLO / ATL</p>
                <div className="p-3 border-b border-white/10 flex gap-1.5">
                  <input data-testid="bolo-input" value={boloText} maxLength={200} onChange={(e) => setBoloText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postBolo()} placeholder="Post BOLO — vehicle, suspect, last seen…"
                    className="flex-1 min-w-0 bg-black/60 border border-orange-500/30 px-2.5 py-2 font-mono text-[9.5px] text-zinc-100 focus:outline-none focus:ring-1 focus:ring-orange-500" />
                  <button data-testid="bolo-post" onClick={postBolo} disabled={busy === "bolo" || boloText.trim().length < 5} className="px-2.5 border border-orange-500/50 text-orange-300 hover:bg-orange-500/10 transition-colors disabled:opacity-40 font-mono text-[9px] uppercase">
                    {busy === "bolo" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Post"}
                  </button>
                </div>
                <div className="p-3 flex flex-col gap-2 max-h-44 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
                  {bolos.map((b) => (
                    <div key={b.id} data-testid={`bolo-${b.id.slice(0, 6)}`} className="border border-orange-500/30 bg-orange-500/5 p-2.5 relative group">
                      <p className="font-mono text-[8px] text-orange-400 uppercase tracking-[0.2em] mb-1">BOLO · {String(b.at).slice(11, 16) || b.at}{b.author ? ` · ${b.author}` : ""}</p>
                      <p className="font-mono text-[9.5px] text-zinc-200 leading-snug pr-4">{b.text}</p>
                      <button data-testid={`bolo-clear-${b.id.slice(0, 6)}`} onClick={() => clearBolo(b.id)} className="absolute top-1.5 right-1.5 text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  {bolos.length === 0 && <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.15em] p-1">No active BOLOs — post one above, it texts every on-duty unit.</p>}
                </div>
                {selAlert && (
                  <div className="flex-1 min-h-0 border-t border-white/10 p-3.5 flex flex-col gap-2 overflow-y-auto" data-testid="cad-detail" style={{ scrollbarWidth: "thin" }}>
                    <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500">Selected call</p>
                    <p className="font-mono text-xs font-bold text-sky-300">{selAlert.ref}</p>
                    <p className="font-mono text-[9px] text-zinc-300 leading-relaxed">{selAlert.summary}</p>
                    <div className="font-mono text-[9px] text-zinc-500 flex flex-col gap-1">
                      <span>opened {String(selAlert.created_at).slice(11, 19)} · elapsed {elapsed(selAlert.created_at)}</span>
                      <span>grid: {selAlert.location?.x != null ? `${Math.round(selAlert.location.x)} / ${Math.round(selAlert.location.y)}` : "per caller"}</span>
                      {selAlert.assigned_unit && <span>assigned: <span className="text-sky-300">{selAlert.assigned_unit}</span></span>}
                    </div>
                    {/* 911 transcript */}
                    {(selAlert.transcript || demo) && (
                      <div className="mt-1 border-t border-white/10 pt-2">
                        <p className="font-mono text-[8px] tracking-[0.25em] uppercase text-zinc-500 flex items-center gap-1.5 mb-1.5"><ScrollText className="w-3 h-3" /> 911 intake transcript</p>
                        <div className="flex flex-col gap-1.5">
                          {(selAlert.transcript ? selAlert.transcript.split("\n") : DEMO_TRANSCRIPT.map((t) => `${t.role === "caller" ? "CALLER" : "DISPATCHER"}: ${t.text}`)).map((line, i) => {
                            const isCaller = line.startsWith("CALLER");
                            return (
                              <p key={i} className={`font-mono text-[9px] leading-snug ${isCaller ? "text-orange-200/90" : "text-sky-200/80"}`}>
                                <span className="text-zinc-500">{isCaller ? "CLR" : "DSP"} ·</span> {line.replace(/^(CALLER|DISPATCHER):\s*/, "")}
                              </p>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {/* call notes — shift handover thread */}
                    <div className="mt-1 border-t border-white/10 pt-2">
                      <p className="font-mono text-[8px] tracking-[0.25em] uppercase text-zinc-500 flex items-center gap-1.5 mb-1.5"><StickyNote className="w-3 h-3" /> Unit notes ({notes.length})</p>
                      <div className="flex flex-col gap-1.5 max-h-28 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
                        {notes.map((n) => (
                          <p key={n.id} className="font-mono text-[9px] leading-snug text-zinc-300">
                            <span className="text-zinc-500">{String(n.at).slice(11, 16)} ·</span> <span className="text-amber-300/90">{n.author}:</span> {n.body}
                          </p>
                        ))}
                        {notes.length === 0 && <p className="font-mono text-[8px] text-zinc-600 uppercase">No notes yet — leave one for the next unit</p>}
                      </div>
                      <div className="flex gap-1.5 mt-1.5">
                        <input data-testid="note-input" value={noteDraft} maxLength={500} onChange={(e) => setNoteDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()}
                          placeholder="Add a note to this call…" className="flex-1 min-w-0 bg-black/60 border border-white/15 px-2 py-1.5 font-mono text-[9px] text-zinc-100 focus:outline-none focus:ring-1 focus:ring-sky-500" />
                        <button data-testid="note-add" onClick={addNote} disabled={!noteDraft.trim()} className="px-2 border border-white/15 text-zinc-300 hover:border-sky-400 hover:text-sky-300 transition-colors disabled:opacity-40 font-mono text-[8px] uppercase">Add</button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="mt-auto border-t border-white/10 px-3.5 py-2.5 font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em]">
                  NC-MDT v4 · court on board · duty clock running
                </div>
              </aside>
            </div>
            </div>
          )}
        </div>

        {/* bottom hardware row: LEDs + hard keys */}
        <div className="absolute bottom-0 inset-x-0 h-[46px] flex items-center justify-center gap-5">
          <span className="absolute left-8 flex items-center gap-2.5">
            <span data-testid="led-power" className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_#34d399]" />
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shadow-[0_0_6px_#38bdf8]" />
            <span className={`w-1.5 h-1.5 rounded-full ${openP1 ? "bg-red-500 shadow-[0_0_8px_#ef4444] animate-pulse" : "bg-zinc-600"}`} />
          </span>
          {["A1", "A2", "◉", "✦", "⌂"].map((k, i) => (
            <button key={k} data-testid={`hardkey-${i}`} onClick={() => i === 2 ? panic() : sfx.keyTick()} title={i === 2 ? "PANIC — double-tap" : undefined} className={`w-8 h-8 rounded-full border shadow-[0_2px_5px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.2)] font-mono text-[9px] flex items-center justify-center transition-colors ${i === 2 ? (panicArm ? "bg-red-600 border-red-400 text-white animate-pulse" : "bg-red-900/60 hover:bg-red-800 border-red-700/70 text-red-200") : "bg-zinc-700 hover:bg-zinc-600 active:bg-zinc-800 border-zinc-500/70 text-zinc-300"}`}>
              {k}
            </button>
          ))}
          <span className="absolute right-8 flex items-center gap-2">
            <Power className="w-3.5 h-3.5 text-zinc-400" />
          </span>
        </div>
      </div>
    </div>
  );
}

/* ---------------- MDT tactical map ---------------- */
const AGENCY_DOT = { police: "#38bdf8", ems: "#34d399", fire: "#fb923c" };

function MapPanel({ data, beats = [], canCommand, drawMode, setDrawMode, onBeatDraw, onBeatEdit }) {
  const [drag, setDrag] = useState(null);
  if (!data) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-sky-400" /></div>;
  const beatPts = beats.flatMap((b) => [[b.zone.x1, b.zone.y1], [b.zone.x2, b.zone.y2]]);
  const pts = [...data.alerts.map((a) => [a.x, a.y]), ...data.units.map((u) => [u.x, u.y]), ...beatPts];
  const empty = pts.length === 0;
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  const pad = 0.12;
  const minX = Math.min(...xs, 0), maxX = Math.max(...xs, 100);
  const minY = Math.min(...ys, 0), maxY = Math.max(...ys, 100);
  const rx = (maxX - minX) || 1, ry = (maxY - minY) || 1;
  const px = (x) => pad * 100 + ((x - minX) / rx) * (100 - pad * 200);
  const py = (y) => pad * 100 + ((y - minY) / ry) * (100 - pad * 200);
  const invX = (p) => minX + ((p - pad * 100) / (100 - pad * 200)) * rx;
  const invY = (p) => minY + ((p - pad * 100) / (100 - pad * 200)) * ry;

  const toPct = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 };
  };
  const down = (e) => { if (drawMode) { const p = toPct(e); setDrag({ a: p, b: p }); } };
  const move = (e) => { if (!drag) return; const p = toPct(e); setDrag((d) => (d ? { ...d, b: p } : d)); };
  const up = () => {
    if (!drag) return;
    const { a, b } = drag;
    setDrag(null);
    if (Math.abs(a.x - b.x) < 2 || Math.abs(a.y - b.y) < 2) return; // too small — ignore
    onBeatDraw({ x1: invX(a.x), y1: invY(a.y), x2: invX(b.x), y2: invY(b.y) });
  };
  const dragRect = drag && { left: Math.min(drag.a.x, drag.b.x), top: Math.min(drag.a.y, drag.b.y), width: Math.abs(drag.a.x - drag.b.x), height: Math.abs(drag.a.y - drag.b.y) };

  return (
    <div data-testid="cad-map" className="flex-1 min-h-0 flex flex-col">
      <div
        onMouseDown={down} onMouseMove={move} onMouseUp={up}
        className={`flex-1 relative m-3 border border-white/10 bg-[#050810] overflow-hidden ${drawMode ? "cursor-crosshair" : ""}`}
        style={{ backgroundImage: "linear-gradient(rgba(56,130,255,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(56,130,255,0.07) 1px, transparent 1px)", backgroundSize: "40px 40px" }}>
        {/* scanline sweep */}
        <div className="absolute inset-x-0 h-16 bg-gradient-to-b from-transparent via-sky-400/[0.05] to-transparent" style={{ animation: "ncScan 6s linear infinite" }} />
        <style>{`@keyframes ncScan { from { transform: translateY(-80px); } to { transform: translateY(110%); } }`}</style>

        {empty && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p data-testid="map-empty" className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.25em] text-center leading-relaxed max-w-sm">
              No plotted fixes — units report positions via the bridge,<br />calls plot when coordinates are attached
            </p>
          </div>
        )}

        {/* patrol beats */}
        {beats.map((b) => (
          <div key={b.id} data-testid={`map-beat-${b.name}`}
            onMouseUp={(e) => { if (!drawMode && canCommand) { e.stopPropagation(); onBeatEdit(b); } }}
            className={`absolute z-[5] border ${canCommand && !drawMode ? "cursor-pointer hover:bg-white/[0.03]" : "pointer-events-none"}`}
            style={{ left: `${px(b.zone.x1)}%`, top: `${py(b.zone.y1)}%`, width: `${px(b.zone.x2) - px(b.zone.x1)}%`, height: `${py(b.zone.y2) - py(b.zone.y1)}%`,
                     borderColor: `${b.color}66`, backgroundColor: `${b.color}0d` }}>
            <span className="absolute -top-2 left-1 font-mono text-[7.5px] font-bold uppercase tracking-[0.15em] px-1 py-0.5 border whitespace-nowrap"
              style={{ color: b.color, borderColor: `${b.color}55`, backgroundColor: "#050810" }}>
              {b.name} · {b.shift}{b.units.length ? ` · ${b.units.join(", ")}` : " · unmanned"}
            </span>
          </div>
        ))}

        {/* in-progress draw rect */}
        {dragRect && (
          <div className="absolute z-[6] border border-dashed border-sky-300 bg-sky-400/10 pointer-events-none"
            style={{ left: `${dragRect.left}%`, top: `${dragRect.top}%`, width: `${dragRect.width}%`, height: `${dragRect.height}%` }} />
        )}

        {/* unit fixes */}
        {data.units.map((u) => (
          <div key={u.callsign} data-testid={`map-unit-${u.callsign}`} className="absolute -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: `${px(u.x)}%`, top: `${py(u.y)}%`, transition: "left 2.8s linear, top 2.8s linear" }}>
            <span className="block w-2.5 h-2.5 rounded-full border-2 border-black" style={{ backgroundColor: AGENCY_DOT[u.role] || AGENCY_DOT.police, boxShadow: `0 0 10px ${AGENCY_DOT[u.role] || AGENCY_DOT.police}` }} />
            <span className="absolute left-3.5 -top-1 font-mono text-[8px] font-bold text-zinc-200 bg-black/70 px-1 py-0.5 whitespace-nowrap border border-white/10">{u.callsign} · {u.status}</span>
          </div>
        ))}

        {/* call pins */}
        {data.alerts.map((a) => {
          const col = (PRI[a.priority] || PRI[3]).bar;
          const isPanic = a.via === "panic" && a.status === "open";
          return (
            <div key={a.id} data-testid={`map-alert-${a.ref}`} className="absolute -translate-x-1/2 -translate-y-1/2 group z-20" style={{ left: `${px(a.x)}%`, top: `${py(a.y)}%` }}>
              {isPanic && (
                <>
                  <span data-testid={`panic-ring-${a.ref}`} className="absolute -inset-3 rounded-full border-2 border-red-500" style={{ animation: "ncPanic 1.4s ease-out infinite" }} />
                  <span className="absolute -inset-6 rounded-full border-2 border-red-500/70" style={{ animation: "ncPanic 1.4s ease-out infinite 0.45s" }} />
                  <span className="absolute -inset-10 rounded-full border border-red-500/40" style={{ animation: "ncPanic 1.4s ease-out infinite 0.9s" }} />
                  <style>{`@keyframes ncPanic { 0% { transform: scale(0.5); opacity: 1; } 100% { transform: scale(2.6); opacity: 0; } }`}</style>
                </>
              )}
              {!isPanic && a.status === "open" && <span className="absolute -inset-2 rounded-full border animate-ping" style={{ borderColor: col }} />}
              <span className={`block w-3 h-3 rotate-45 border-2 border-black ${isPanic ? "animate-pulse" : ""}`} style={{ backgroundColor: isPanic ? "#ef4444" : col, boxShadow: `0 0 12px ${isPanic ? "#ef4444" : col}` }} />
              <span className="hidden group-hover:block absolute left-4 -top-2 w-56 font-mono text-[8.5px] text-zinc-200 bg-black/90 border border-white/15 p-2 leading-snug z-30">
                <span className="font-bold" style={{ color: isPanic ? "#ef4444" : col }}>{a.ref} · {isPanic ? "10-99 PANIC — UNACKNOWLEDGED" : a.status.toUpperCase()}</span><br />{a.summary}
              </span>
            </div>
          );
        })}

        {/* corner frame ticks */}
        {["top-1 left-1 border-t-2 border-l-2", "top-1 right-1 border-t-2 border-r-2", "bottom-1 left-1 border-b-2 border-l-2", "bottom-1 right-1 border-b-2 border-r-2"].map((c) => (
          <span key={c} className={`absolute ${c} w-4 h-4 border-sky-500/40`} />
        ))}
      </div>
      <div className="flex items-center gap-4 px-4 pb-2.5 font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: AGENCY_DOT.police }} /> Police</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: AGENCY_DOT.ems }} /> EMS</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: AGENCY_DOT.fire }} /> Fire</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rotate-45 bg-red-500 inline-block" /> Call pin (hover for detail)</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 border border-fuchsia-400/60 bg-fuchsia-400/10 inline-block" /> Beat ({beats.length})</span>
        {canCommand && (
          <button data-testid="map-draw-beat" onClick={() => setDrawMode(!drawMode)}
            className={`font-mono text-[8px] uppercase tracking-[0.15em] border px-2 py-1 transition-colors flex items-center gap-1 ${drawMode ? "border-sky-400 text-sky-300 bg-sky-500/15" : "border-white/15 text-zinc-400 hover:border-sky-400 hover:text-sky-300"}`}>
            <Hexagon className="w-3 h-3" /> {drawMode ? "Drag on map… (cancel)" : "Draw beat"}
          </button>
        )}
        <span className="ml-auto">{data.units.length} units · {data.alerts.length} plotted calls</span>
      </div>
    </div>
  );
}
